import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { Fragment } from 'react';

const getPanelPaddingTop = (theme: Theme) => `calc(${theme.spacing(4)} + 10vh)`;

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      position: 'relative',
      flexGrow: 1,
      flexDirection: 'column',
      justifyContent: 'flex-start',
      display: 'flex',
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      [theme.breakpoints.up('md')]: {
        paddingTop: getPanelPaddingTop(theme),
        overflowY: 'auto',
      },
    },
    grid: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      [theme.breakpoints.up('md')]: {
        display: 'grid',
        gridTemplateColumns: 'min-content auto',
        gridTemplateRows: 'auto auto',
        gridColumnGap: theme.spacing(12),
        gridTemplateAreas: `
          "cardImage textContent"
          ". textContent"
        `,
      },
    },
    textContentArea: {
      gridArea: 'textContent',
      overflowX: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '100%',
      [theme.breakpoints.down('md')]: {
        alignItems: 'center',
      },
      [theme.breakpoints.up('md')]: {
        justifySelf: 'stretch',
        alignSelf: 'stretch',
      },
    },
    cardImageArea: {
      gridArea: 'cardImage',
      [theme.breakpoints.down('md')]: {
        marginBottom: theme.spacing(4),
      },
    },
    staticCard: {
      [theme.breakpoints.up('md')]: {
        visibility: 'hidden',
      },
    },
    fixedCardWrapper: {
      position: 'absolute',
      top: getPanelPaddingTop(theme),
      left: 0,
      width: '100%',
      pointerEvents: 'none',
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    fixedCard: {
      pointerEvents: 'auto',
      position: 'fixed',
    },
    buttonContentArea: {
      marginTop: theme.spacing(4),
      '&:empty': {
        display: 'none',
      },
    },
    title: {
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down('md')]: {
        textAlign: 'center',
      },
      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing(4),
      },
    },
  }),
  { name: 'PanelLayout' },
);

interface PanelLayoutProps {
  title?: string;
  cardImageContent?: React.ReactNode;
  formContent?: React.ReactNode;
  buttonsContent?: React.ReactNode;
  value: number;
  index: number;
  children?: React.ReactNode;
}

const PanelLayout = (props: PanelLayoutProps) => {
  const classes = useStyles();
  const { value, index, title, cardImageContent, formContent, buttonsContent } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`full-width-tabpanel-${index}`} aria-labelledby={`full-width-tab-${index}`} className={classes.root}>
      {value === index && (
        <Fragment>
          <div className={classes.grid}>
            <div className={classes.cardImageArea}>
              <div className={classes.staticCard}>{cardImageContent}</div>
            </div>
            <div className={classes.textContentArea}>
              <div>
                <Typography variant="h1" className={classes.title}>
                  {title}
                </Typography>
                <div>{formContent}</div>
              </div>
              {buttonsContent && <div className={classes.buttonContentArea}>{buttonsContent}</div>}
            </div>
          </div>
          <div className={classes.fixedCardWrapper}>
            <div className={classes.fixedCard}>{cardImageContent}</div>
          </div>
        </Fragment>
      )}
    </div>
  );
};

export default PanelLayout;
